import { useCallback, useEffect } from "react";
import type { ToolDataEntry } from "@/config/registries/toolRegistry";
import {
  isViewingConversation,
  markConversationUnread,
} from "@/features/chat/stream/unread";
import type { IMessage } from "@/lib/db/chatDb";
import { db } from "@/lib/db/chatDb";
import { streamLog } from "@/lib/streamLogger";
import { wsManager } from "@/lib/websocket/WebSocketManager";
import { useChatStore } from "@/stores/chatStore";
import { useStreamStore } from "@/stores/streamStore";

/**
 * WebSocket payload for background executor notifications.
 * Delivered when an executor (live or queued) completes and generates
 * a notification as a NEW bot message.
 */
interface BgMessageEvent {
  type: "conversation.new_message";
  conversation_id: string;
  message: {
    type: "bot";
    response: string;
    message_id: string;
    date: string;
    task_id?: string;
    tool_data?: ToolDataEntry[];
    follow_up_actions?: string[];
    replyToMessage?: {
      id: string;
      content: string;
      role: "user" | "assistant";
    };
  };
}

/**
 * Subscribe to `conversation.new_message` WebSocket events.
 *
 * When an executor finishes (live or queued), the backend saves a NEW bot
 * message to MongoDB and pushes it here via WebSocket. This hook inserts
 * the message into IndexedDB and the Zustand store so the chat view
 * updates immediately — no page reload needed.
 *
 * Conversation-scoped: only updates the active chat view. Messages for
 * other conversations are persisted to IndexedDB (visible on navigation).
 */
export function useBgMessageWebSocket() {
  const handleBgMessage = useCallback(async (raw: unknown) => {
    const event = raw as BgMessageEvent;
    const { conversation_id, message } = event;

    if (!conversation_id || !message?.message_id) {
      return;
    }

    // Remove streaming placeholder (id === task_id) if one exists in the store.
    // The placeholder was created by useExecutorStream for live tool progress;
    // now the final message is here, so the placeholder is no longer needed.
    if (message.task_id) {
      const state = useChatStore.getState();
      const msgs = state.messagesByConversation[conversation_id] ?? [];
      const hasPlaceholder = msgs.some((m) => m.id === message.task_id);
      if (hasPlaceholder) {
        useChatStore.getState().removeMessage(message.task_id, conversation_id);
      }
    }

    // Build IMessage for IndexedDB
    const iMessage: IMessage = {
      id: message.message_id,
      conversationId: conversation_id,
      content: message.response,
      role: "assistant",
      status: "sent",
      createdAt: new Date(message.date),
      updatedAt: new Date(message.date),
      messageId: message.message_id,
      tool_data: message.tool_data ?? null,
      follow_up_actions: message.follow_up_actions ?? null,
      replyToMessageData: message.replyToMessage ?? null,
    };

    // Persist to IndexedDB (dbEventEmitter syncs to store automatically).
    // When this final message corresponds to a queued executor placeholder
    // (keyed by task_id, persisted by useExecutorStream), replace it atomically
    // so the placeholder is removed from IndexedDB and not left as a duplicate.
    try {
      if (message.task_id) {
        await db.replaceMessage(message.task_id, iMessage);
      } else {
        await db.putMessage(iMessage);
      }
    } catch (err) {
      console.error("[useBgMessageWebSocket] Failed to persist message:", err);
    }

    // Also update store directly for immediate render if viewing this conversation
    if (isViewingConversation(conversation_id)) {
      useChatStore.getState().addOrUpdateMessage(iMessage);
    } else {
      // A background result landing in a conversation the user isn't viewing
      // surfaces as unread in the sidebar (cleared by ChatPage's mark-as-read).
      markConversationUnread(conversation_id);
    }

    // End the awaiting-executor session AFTER the result message is in the
    // store, so loading hands off to the rendered result in the same frame — no
    // flash of "loading gone" before the result appears.
    const stream = useStreamStore.getState();
    if (stream.sessions[conversation_id]?.phase === "awaiting_executor") {
      stream.endSession(conversation_id);
    }
    streamLog("ws", "conversation.new_message", {
      conversationId: conversation_id,
      detail: { task_id: message.task_id },
    });
  }, []);

  useEffect(() => {
    wsManager.on("conversation.new_message", handleBgMessage);
    return () => {
      wsManager.off("conversation.new_message", handleBgMessage);
    };
  }, [handleBgMessage]);
}
